import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Bell, Settings, Users, Mail, Plus, Edit2, Trash2, Send, Clock, CheckCircle, RefreshCw, Sparkles, Save } from 'lucide-react';

const UserReminders = () => {
    const [activeTab, setActiveTab] = useState('features');
    const [loading, setLoading] = useState(false);
    const [features, setFeatures] = useState([]);
    const [settings, setSettings] = useState({});
    const [inactiveUsers, setInactiveUsers] = useState([]);
    const [history, setHistory] = useState([]);
    const [historyPagination, setHistoryPagination] = useState({ page: 1, totalPages: 1 });
    const [showFeatureModal, setShowFeatureModal] = useState(false);
    const [editingFeature, setEditingFeature] = useState(null);
    const [featureForm, setFeatureForm] = useState({ title: '', description: '', is_active: true, display_order: 0 });
    const [sendingReminder, setSendingReminder] = useState(false);

    useEffect(() => {
        if (activeTab === 'features') fetchFeatures();
        else if (activeTab === 'settings') fetchSettings();
        else if (activeTab === 'inactive') fetchInactiveUsers();
        else if (activeTab === 'history') fetchHistory();
    }, [activeTab]);

    const fetchFeatures = async () => {
        setLoading(true);
        try { const res = await axios.get('/api/user-reminders/features'); setFeatures(res.data); }
        catch (e) { toast.error('Failed to fetch features'); }
        finally { setLoading(false); }
    };

    const fetchSettings = async () => {
        setLoading(true);
        try { const res = await axios.get('/api/user-reminders/settings'); setSettings(res.data); }
        catch (e) { toast.error('Failed to fetch settings'); }
        finally { setLoading(false); }
    };

    const fetchInactiveUsers = async () => {
        setLoading(true);
        try { const res = await axios.get('/api/user-reminders/inactive-users'); setInactiveUsers(res.data.users || []); }
        catch (e) { toast.error('Failed to fetch inactive users'); }
        finally { setLoading(false); }
    };

    const fetchHistory = async (page = 1) => {
        setLoading(true);
        try {
            const res = await axios.get('/api/user-reminders/history?page=' + page);
            setHistory(res.data.data || []);
            setHistoryPagination(res.data.pagination || { page: 1, totalPages: 1 });
        } catch (e) { toast.error('Failed to fetch history'); }
        finally { setLoading(false); }
    };

    const handleSaveFeature = async (e) => {
        e.preventDefault();
        try {
            if (editingFeature) {
                await axios.put('/api/user-reminders/features/' + editingFeature.id, featureForm);
                toast.success('Feature updated');
            } else {
                await axios.post('/api/user-reminders/features', featureForm);
                toast.success('Feature created');
            }
            setShowFeatureModal(false);
            setEditingFeature(null);
            setFeatureForm({ title: '', description: '', is_active: true, display_order: 0 });
            fetchFeatures();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    };

    const handleEditFeature = (f) => {
        setEditingFeature(f);
        setFeatureForm({ title: f.title, description: f.description || '', is_active: f.is_active, display_order: f.display_order });
        setShowFeatureModal(true);
    };

    const handleDeleteFeature = async (id) => {
        if (!window.confirm('Delete this feature?')) return;
        try { await axios.delete('/api/user-reminders/features/' + id); toast.success('Deleted'); fetchFeatures(); }
        catch (e) { toast.error('Failed to delete'); }
    };

    const handleUpdateSetting = async (key, value) => {
        try {
            await axios.put('/api/user-reminders/settings/' + key, { value });
            toast.success('Setting updated');
            setSettings(prev => ({ ...prev, [key]: { ...prev[key], value } }));
        } catch (e) { toast.error('Failed to update setting'); }
    };

    const handleSendReminder = async (userId) => {
        if (!window.confirm('Send reminder?')) return;
        setSendingReminder(true);
        try { await axios.post('/api/user-reminders/send/' + userId); toast.success('Sent!'); fetchInactiveUsers(); }
        catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
        finally { setSendingReminder(false); }
    };

    const handleSendAllReminders = async () => {
        if (!window.confirm('Send to all ' + inactiveUsers.length + ' users?')) return;
        setSendingReminder(true);
        try { const res = await axios.post('/api/user-reminders/send-all'); toast.success(res.data.message); fetchInactiveUsers(); }
        catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
        finally { setSendingReminder(false); }
    };

    const tabs = [
        { id: 'features', label: 'Features', icon: Sparkles },
        { id: 'settings', label: 'Settings', icon: Settings },
        { id: 'inactive', label: 'Inactive Users', icon: Users },
        { id: 'history', label: 'History', icon: Clock }
    ];

    return (
        <div className="page-content">
            <div className="page-header">
                <div>
                    <h1 className="page-title">User Reminders</h1>
                    <p style={{ color: '#64748b', margin: '8px 0 0 0' }}>Manage features and automatic reminder emails</p>
                </div>
                <button className="btn btn-outline" onClick={() => { if (activeTab === 'features') fetchFeatures(); else if (activeTab === 'settings') fetchSettings(); else if (activeTab === 'inactive') fetchInactiveUsers(); else if (activeTab === 'history') fetchHistory(); }}>
                    <RefreshCw size={18} /> Refresh
                </button>
            </div>

            <div className="card" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid var(--border-color)' }}>
                    {tabs.map(tab => { const Icon = tab.icon; return (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={'btn ' + (activeTab === tab.id ? 'btn-primary' : 'btn-outline')} style={{ borderRadius: 0, borderBottom: activeTab === tab.id ? '2px solid var(--primary-color)' : 'none', marginBottom: '-2px' }}>
                            <Icon size={18} style={{ marginRight: '8px' }} />{tab.label}
                        </button>
                    ); })}
                </div>
            </div>

            {activeTab === 'features' && (
                <div className="card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 className="card-title"><Sparkles size={20} /> System Features</h3>
                        <button className="btn btn-primary" onClick={() => { setEditingFeature(null); setFeatureForm({ title: '', description: '', is_active: true, display_order: 0 }); setShowFeatureModal(true); }}>
                            <Plus size={18} /> Add Feature
                        </button>
                    </div>
                    <p style={{ padding: '0 20px', color: '#64748b', fontSize: '14px' }}>Features highlighted in reminder emails.</p>
                    {loading ? <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div> : (
                        <div className="table-responsive">
                            <table className="table">
                                <thead><tr><th>Order</th><th>Title</th><th>Description</th><th>Status</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
                                <tbody>
                                    {features.map(f => (
                                        <tr key={f.id}>
                                            <td>{f.display_order}</td>
                                            <td><strong>{f.title}</strong></td>
                                            <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.description || '-'}</td>
                                            <td><span className={'badge ' + (f.is_active ? 'badge-success' : 'badge-secondary')}>{f.is_active ? 'Active' : 'Inactive'}</span></td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditFeature(f)} style={{ marginRight: '8px' }}><Edit2 size={16} /></button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteFeature(f.id)}><Trash2 size={16} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                    {features.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No features yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'settings' && (
                <div className="card">
                    <div className="card-header"><h3 className="card-title"><Settings size={20} /> Reminder Settings</h3></div>
                    <div style={{ padding: '20px' }}>
                        {loading ? <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div> : (
                            <div style={{ display: 'grid', gap: '20px', maxWidth: '600px' }}>
                                <div className="form-group">
                                    <label className="form-label">Enable Automatic Reminders</label>
                                    <select className="form-control" value={settings.reminder_enabled?.value || 'false'} onChange={(e) => handleUpdateSetting('reminder_enabled', e.target.value)}>
                                        <option value="true">Enabled</option><option value="false">Disabled</option>
                                    </select>
                                    <p className="form-hint">Reminders sent daily at 9:00 AM when enabled</p>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Days of Inactivity</label>
                                    <input type="number" className="form-control" value={settings.inactive_days_threshold?.value || '14'} onChange={(e) => handleUpdateSetting('inactive_days_threshold', e.target.value)} min="1" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Min Days Between Reminders</label>
                                    <input type="number" className="form-control" value={settings.reminder_frequency_days?.value || '7'} onChange={(e) => handleUpdateSetting('reminder_frequency_days', e.target.value)} min="1" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Max Reminders Per User</label>
                                    <input type="number" className="form-control" value={settings.max_reminders_per_user?.value || '3'} onChange={(e) => handleUpdateSetting('max_reminders_per_user', e.target.value)} min="1" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Email Subject</label>
                                    <input type="text" className="form-control" value={settings.reminder_subject?.value || ''} onChange={(e) => handleUpdateSetting('reminder_subject', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">System Name</label>
                                    <input type="text" className="form-control" value={settings.system_name?.value || 'DynaFinances'} onChange={(e) => handleUpdateSetting('system_name', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Login URL</label>
                                    <input type="url" className="form-control" value={settings.login_url?.value || ''} onChange={(e) => handleUpdateSetting('login_url', e.target.value)} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'inactive' && (
                <div className="card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 className="card-title"><Users size={20} /> Inactive Users ({inactiveUsers.length})</h3>
                        {inactiveUsers.length > 0 && <button className="btn btn-primary" onClick={handleSendAllReminders} disabled={sendingReminder}><Send size={18} /> Send All</button>}
                    </div>
                    {loading ? <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div> : (
                        <div className="table-responsive">
                            <table className="table">
                                <thead><tr><th>User</th><th>Company</th><th>Last Login</th><th>Reminders</th><th>Last Reminder</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
                                <tbody>
                                    {inactiveUsers.map(u => (
                                        <tr key={u.id}>
                                            <td><strong>{u.first_name} {u.last_name}</strong><br/><small style={{ color: '#64748b' }}>{u.email}</small></td>
                                            <td>{u.company_name || '-'}</td>
                                            <td>{u.last_login ? new Date(u.last_login).toLocaleDateString() : <span style={{ color: '#ef4444' }}>Never</span>}</td>
                                            <td>{u.reminders_sent || 0}</td>
                                            <td>{u.last_reminder_sent ? new Date(u.last_reminder_sent).toLocaleDateString() : '-'}</td>
                                            <td style={{ textAlign: 'right' }}><button className="btn btn-sm btn-primary" onClick={() => handleSendReminder(u.id)} disabled={sendingReminder}><Mail size={16} /> Send</button></td>
                                        </tr>
                                    ))}
                                    {inactiveUsers.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}><CheckCircle size={48} style={{ marginBottom: '16px', color: '#22c55e' }} /><br/>All users are active!</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'history' && (
                <div className="card">
                    <div className="card-header"><h3 className="card-title"><Clock size={20} /> Reminder History</h3></div>
                    {loading ? <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div> : (
                        <div className="table-responsive">
                            <table className="table">
                                <thead><tr><th>Sent At</th><th>User</th><th>Email</th><th>Type</th><th>Features</th></tr></thead>
                                <tbody>
                                    {history.map(item => (
                                        <tr key={item.id}>
                                            <td>{new Date(item.sent_at).toLocaleString()}</td>
                                            <td>{item.first_name} {item.last_name}</td>
                                            <td>{item.email}</td>
                                            <td><span className="badge badge-info">{item.reminder_type}</span></td>
                                            <td style={{ maxWidth: '200px' }}>{item.features_included?.join(', ') || '-'}</td>
                                        </tr>
                                    ))}
                                    {history.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No reminders sent yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {showFeatureModal && (
                <div className="modal-overlay" onClick={() => setShowFeatureModal(false)}>
                    <div className="modal-content" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header-v2" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)' }}>
                            <div className="modal-header-v2-icon"><Sparkles size={28} /></div>
                            <div>
                                <h2 className="modal-title-v2">{editingFeature ? 'Edit Feature' : 'Add New Feature'}</h2>
                                <p className="modal-subtitle-v2">This will appear in reminder emails</p>
                            </div>
                            <button className="btn-close-v2" onClick={() => setShowFeatureModal(false)}>x</button>
                        </div>
                        <form onSubmit={handleSaveFeature}>
                            <div className="modal-body-v2">
                                <div className="form-group">
                                    <label className="form-label">Title *</label>
                                    <input type="text" className="form-control" value={featureForm.title} onChange={e => setFeatureForm({ ...featureForm, title: e.target.value })} placeholder="e.g., Automated Invoice Reminders" required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" value={featureForm.description} onChange={e => setFeatureForm({ ...featureForm, description: e.target.value })} placeholder="Brief description..." rows={3} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="form-group">
                                        <label className="form-label">Display Order</label>
                                        <input type="number" className="form-control" value={featureForm.display_order} onChange={e => setFeatureForm({ ...featureForm, display_order: parseInt(e.target.value) || 0 })} min="0" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Status</label>
                                        <select className="form-control" value={featureForm.is_active} onChange={e => setFeatureForm({ ...featureForm, is_active: e.target.value === 'true' })}>
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-v2">
                                <button type="button" className="btn btn-secondary-v2" onClick={() => setShowFeatureModal(false)}>Cancel</button>
                                <button type="submit" className="btn btn-primary-v2"><Save size={18} /> {editingFeature ? 'Update' : 'Create'} Feature</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserReminders;
